import { useState, useRef } from "react";
import { useUploadFile } from "../api/files";
import { showError } from "../shared/toast";
import "./image-upload.css";

interface ImageUploadProps {
    value?: string;
    onChange?: (url: string) => void;
    disabled?: boolean;
    maxSizeMB?: number;
}

export const ImageUpload = ({
    value,
    onChange,
    disabled = false,
    maxSizeMB = 5,
}: ImageUploadProps): JSX.Element => {
    const [preview, setPreview] = useState<string | null>(value || null);
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const uploadMutation = useUploadFile();

    const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
        if (!allowedTypes.includes(file.type)) {
            showError("Ogiltig filtyp", "Endast JPG, PNG och WebP är tillåtna");
            return;
        }

        // Validate file size
        const maxSizeBytes = maxSizeMB * 1024 * 1024;
        if (file.size > maxSizeBytes) {
            showError("Filen är för stor", `Max storlek: ${maxSizeMB} MB`);
            return;
        }

        // Show preview
        const reader = new FileReader();
        reader.onload = (e) => {
            setPreview(e.target?.result as string);
        };
        reader.readAsDataURL(file);

        // Upload file
        setIsUploading(true);
        try {
            const result = await uploadMutation.mutateAsync(file);
            onChange?.(result.fileUrl);
        } catch (error: any) {
            const message = error?.response?.data?.message || "Kunde inte ladda upp bilden";
            showError("Uppladdning misslyckades", message);
            setPreview(null);
        } finally {
            setIsUploading(false);
            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        }
    };

    const handleRemove = () => {
        setPreview(null);
        onChange?.("");
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    return (
        <div className="image-upload">
            <div className="image-upload__preview">
                {preview ? (
                    <div className="image-upload__preview-container">
                        <img src={preview} alt="Preview" className="image-upload__preview-image" />
                        {!disabled && (
                            <button
                                type="button"
                                className="image-upload__remove-button"
                                onClick={handleRemove}
                                aria-label="Ta bort bild"
                            >
                                ×
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="image-upload__placeholder">
                        <span>Ingen bild vald</span>
                    </div>
                )}
            </div>
            {!disabled && (
                <div className="image-upload__controls">
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={handleFileSelect}
                        disabled={isUploading}
                        className="image-upload__input"
                        id="image-upload-input"
                    />
                    <label
                        htmlFor="image-upload-input"
                        className={`image-upload__label ${isUploading ? "uploading" : ""}`}
                    >
                        {isUploading ? "Laddar upp..." : "Välj bild"}
                    </label>
                    <p className="image-upload__hint">
                        Max storlek: {maxSizeMB} MB. Tillåtna format: JPG, PNG, WebP
                    </p>
                </div>
            )}
        </div>
    );
};